import { useState } from "react";
import Reveal from "./Reveal";
import VideoLightbox from "./VideoLightbox";
import { ArrowUpRight, Sparkles, Expand } from "lucide-react";

const VIDEO_VANS = "/videos/bestelwagens-personenwagens.mp4";
const VIDEO_TRUCKS = "/videos/vrachtwagens.mp4";

const SERVICES = [
  {
    nr: "01",
    title: "Personenwagens & Bestelwagens",
    short: "Voor zaakvoerders, kaderleden en medewerkers.",
    description:
      "Of het nu gaat om de wagen van uw directie of de bestelwagens van uw technische ploeg, wij houden ze fris, verzorgd en representatief. Interieur en exterieur worden periodiek behandeld op uw locatie, volgens een planning die wij beheren.",
    video: VIDEO_VANS,
  },
  {
    nr: "02",
    title: "Vrachtwagens",
    short: "Trekkers, opleggers, fleet washing.",
    description:
      "Wij zorgen ervoor dat vrachtwagens er steeds representatief bij staan, zonder impact op de operationele planning. Van trekkers tot opleggers: wij reinigen op locatie en stemmen onze interventies af op uw planning, zodat voertuigen maximaal inzetbaar blijven.",
    video: VIDEO_TRUCKS,
  },
];

export default function Services() {
  const [active, setActive] = useState(null);

  const open = (svc) => setActive(svc);
  const close = () => setActive(null);

  return (
    <section
      id="diensten"
      data-testid="services-section"
      className="relative bg-cgsurface py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
            <div className="max-w-xl">
              <p className="eyebrow mb-5" data-testid="services-eyebrow">Onze expertise</p>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-cgdark">
                Diensten <span className="italic font-light">op maat</span> <br />
                van uw vloot.
              </h2>
            </div>
            <p className="text-cgmuted max-w-sm text-base leading-relaxed">
              Van directiewagen tot vrachtwagen: wij stemmen onze aanpak af op
              elk type voertuig en elke gewenste frequentie. Klik op de video&apos;s om ze in volledig scherm te bekijken.
            </p>
          </div>
        </Reveal>

        {/* Two main video cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal
              key={s.title}
              delay={i * 90}
              className="group bg-white border border-cgborder overflow-hidden flex flex-col"
              data-testid={`service-card-${i}`}
            >
              <button
                type="button"
                onClick={() => open(s)}
                className="relative overflow-hidden aspect-[3/4] bg-cgdark block w-full text-left cursor-zoom-in"
                data-testid={`service-video-button-${i}`}
                aria-label={`Bekijk ${s.title} video in volledig scherm`}
              >
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  data-testid={`service-video-${i}`}
                >
                  <source src={s.video} type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-cgdark/85 via-cgdark/10 to-cgdark/30 pointer-events-none" />

                <div className="absolute top-5 left-5 right-5 flex items-center justify-between pointer-events-none">
                  <span className="text-white font-heading text-sm tracking-[0.2em]">{s.nr}</span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-semibold tracking-[0.2em] uppercase">
                    <Expand size={12} /> Klik voor fullscreen
                  </span>
                </div>

                <div className="absolute bottom-5 left-5 right-5 pointer-events-none">
                  <h3 className="font-heading text-white text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                    {s.title}
                  </h3>
                </div>

                {/* Play hint center */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="w-20 h-20 rounded-full bg-cgprimary/90 backdrop-blur-md flex items-center justify-center shadow-2xl">
                    <Expand size={28} className="text-white" strokeWidth={1.5} />
                  </div>
                </div>
              </button>

              <div className="p-8 md:p-10 flex flex-col flex-1">
                <p className="text-cgprimary text-sm font-semibold tracking-wide mb-3">
                  {s.short}
                </p>
                <p className="text-cgmuted leading-relaxed text-[15px] mb-6 flex-1">
                  {s.description}
                </p>
                <a
                  href="#contact"
                  className="inline-flex items-center justify-between border-t border-cgborder pt-5 text-cgdark group-hover:text-cgprimary transition-colors"
                  data-testid={`service-cta-${i}`}
                >
                  <span className="font-semibold text-[13px] tracking-[0.15em] uppercase">
                    Vraag offerte aan
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Dieptereiniging strip */}
        <Reveal delay={180}>
          <div
            className="mt-6 md:mt-8 bg-cgdark text-white relative overflow-hidden group"
            data-testid="service-dieptereiniging"
          >
            <div className="absolute inset-0 grain opacity-20" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-cgprimary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

            <div className="relative grid md:grid-cols-12 gap-8 p-8 md:p-12 lg:p-16 items-center">
              <div className="md:col-span-2 flex md:justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center border border-white/15 group-hover:border-cgprimary group-hover:bg-cgprimary/10 transition-all duration-500">
                  <Sparkles size={28} className="text-cgprimary" strokeWidth={1.5} />
                </div>
              </div>

              <div className="md:col-span-7">
                <p className="eyebrow mb-3" data-testid="service-dieptereiniging-eyebrow">
                  Extra dienst · 03
                </p>
                <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                  Dieptereiniging
                </h3>
                <p className="text-white/70 leading-relaxed text-[15px] md:text-base max-w-2xl">
                  Voor voertuigen die meer nodig hebben dan een standaard reiniging.
                  Wij voeren grondige interieurreinigingen uit, verwijderen hardnekkig vuil
                  en herstellen een verzorgde, hygiënische werkomgeving voor chauffeurs en medewerkers.
                  Beschikbaar voor alle types voertuigen.
                </p>
              </div>

              <div className="md:col-span-3 md:text-right">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 text-white font-semibold text-[13px] tracking-[0.15em] uppercase border-b border-white/30 hover:border-cgprimary hover:text-cgprimary transition-colors pb-2"
                  data-testid="service-dieptereiniging-cta"
                >
                  Plan een dieptereiniging
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <VideoLightbox
        open={!!active}
        src={active?.video}
        title={active?.title}
        onClose={close}
      />
    </section>
  );
}
